//libs
import get from 'lodash/get';
import { Location } from 'history';
import isEmpty from 'lodash/isEmpty';

//actions
import { ActionTypes } from './actions';

//helpers
import { fetchListingData } from 'shared/api/listingV2';
import { validateCrossRedirection } from 'shared/components/AppRedirectionPopup/components/AppRedirectionPopup/helpers';
import {
  getIsFilteredURL,
  isSearchPage,
  transformPayload,
  appendURLParameters,
  isOfferPage,
} from './helpers';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import logger from 'shared/logger';

//types
import { getLandingPageData, LandingPageProps } from 'shared/api/landingpage';
import { getFilterNames, transformApiResult } from './transformer';

//selectors
import {
  getPayload,
  getLocation,
  getIsInitial,
  getProducts,
  getIsFetchingWithFilters,
} from 'shared/store/listingV2/selectors';
import { getSSRHeaders } from 'shared/store/app/selectors';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';

//constants
import { API_KEYS } from './constants';
import { RENDER_MODE } from 'shared/constants';

//actions
import { redirect, setAppSearchTerm, updateStatusCode } from 'shared/store/app/actions';

export const getPLPData = (isMobile: boolean) => {
  return async (dispatch: any, getState: any) => {
    try {
      const state = getState();
      const ssrHeaders = getSSRHeaders(state);
      const platformKey = getDeviceType() === RENDER_MODE.DESKTOP ? 'dweb.enabled' : 'mweb.enabled';
      const isAdPlatformEnabled = get(getRemoteConfig(state, 'plp_adplatform'), platformKey, false);

      const isInitial = getIsInitial(state);
      const previousProducts = getProducts(state);
      const isFetchingWithFilters = getIsFetchingWithFilters(state);
      const isPriceRevealEnabled = get(getRemoteConfig(state, 'priceReveal'), 'desktop', false);
      const payload: Record<string, string> = getPayload(state);
      if (isPriceRevealEnabled) payload[API_KEYS.PRICE_REVEAL] = '1';

      const location = getLocation(state) as Location;

      const isSearch = isSearchPage(location.pathname);
      const params = transformPayload(payload, location, isMobile);

      if (!params) {
        return;
      }
      if (params.defaultParams[API_KEYS.SEARCH_TERM])
        dispatch(setAppSearchTerm(params.defaultParams[API_KEYS.SEARCH_TERM]));

      if (isInitial) params.defaultParams['meta'] = 1;

      const listingData = await fetchListingData(
        { ...params.defaultParams, currentPage: params.currentPage, ...params.filters },
        ssrHeaders
      );
      let adPlatform;

      const baseCategoryUrl = get(listingData, 'response.categoryURL');
      const redirectPageParams = get(listingData, 'response.redirect_page', {});
      const redirectURL = get(listingData, 'response.redirect_url.value');

      if (isEmpty(redirectPageParams) === false) {
        const isValidCrossRedirect = validateCrossRedirection(redirectPageParams);

        if (isValidCrossRedirect) {
          const redirectConfig = {
            enabled: true,
            redirectURL: redirectPageParams.id,
          };

          dispatch({
            type: ActionTypes.UPDATE_CROSS_PLATFORM_REDIRECT_CONFIG,
            payload: redirectConfig,
          });
        }
      }

      // redirect to respective URL provided by PLP API
      if (isEmpty(redirectURL) === false) {
        const redirectWithTracking = appendURLParameters(
          redirectURL,
          `${location.search}&searchRedirection=1`
        );

        dispatch(redirect(redirectWithTracking));
        return;
      }

      if (baseCategoryUrl && baseCategoryUrl !== location.pathname) {
        dispatch(redirect(`${baseCategoryUrl}${location.search}`, 301));
        logger.info({
          message: `Redirection: ${location.pathname} : ${baseCategoryUrl}`,
        });

        return;
      }
      let footerHTML = null;

      if (isInitial) {
        const pageName = get(listingData, 'response.ad_platform_data.page-type', '');
        const pageData = get(listingData, 'response.ad_platform_data.page-data', '');
        const count = get(listingData, 'response.count', 0);
        footerHTML = get(listingData, 'response.block_data', 0);

        const adPlatformRequestParams: LandingPageProps = {
          headers: ssrHeaders,
          pageName,
        };

        if (!(count === 0 && !getIsFilteredURL(location.pathname))) {
          adPlatformRequestParams.pageData = pageData;
        }
        if (isAdPlatformEnabled && pageName) {
          adPlatform = await getLandingPageData(adPlatformRequestParams);
        }
      }

      const filters = get(listingData, 'response.filters', []);
      let filterWithNames = [];
      filterWithNames = getFilterNames(filters, params.filters);

      if (params.currentPage !== '1' && listingData.response.products.length === 0) {
        const url = baseCategoryUrl
          ? baseCategoryUrl
          : location.pathname + location.search.split('&p=')[0];

        dispatch(redirect(url, 307));
        return;
      }

      dispatch({
        type: ActionTypes.SET_LISTING_DATA,
        payload: transformApiResult(
          {
            listingResponse: listingData.response,
            adPlatformResponse: adPlatform,
            currentPage: params.currentPage,
            appliedFilters: params.filters,
            extras: {
              isSearch,
              isAdPlatformEnabled,
              ...params.defaultParams,
              filterWithNames,
              footerHTML,
              isOfferPage: isOfferPage(location.pathname),
            },
          },
          isFetchingWithFilters,
          { products: previousProducts }
        ),
      });
    } catch (e) {
      if (e.status === 404) {
        dispatch({
          type: ActionTypes.SET_LISTING_ERROR,
          payload: {
            isFetchingError: false,
            isNotFound: true,
          },
        });
        dispatch(updateStatusCode(404));
      } else {
        dispatch({
          type: ActionTypes.SET_LISTING_ERROR,
          payload: {
            isFetchingError: true,
            isNotFound: false,
          },
        });
        dispatch(updateStatusCode(500));
      }
    }
    return;
  };
};
